import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { useLandingT, usePageVars } from './kit';
import type { LandingCopy } from './pages';

/** Why this page's kind of recording works well here, written for the page. */
export default function PageDetails({ page }: { page: LandingCopy }) {
  const { t } = useLandingT();
  const vars = usePageVars(page);
  const reveal = useReveal<HTMLUListElement>(0.2);
  const { details } = page;

  return (
    <section className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
      <Stage
        variant='lavender'
        halftoneAt='100% 0%'
        halftoneReach={50}
        className='px-6 py-14 sm:px-12 sm:py-16'
      >
        <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
          <div className='lg:col-span-5'>
            <SectionHeading
              size='md'
              title={t(details.title, vars)}
              tail={details.tail ? t(details.tail, vars) : undefined}
            />
          </div>
          <ul
            ref={reveal.ref}
            data-reveal={reveal.state}
            role='list'
            className={clsxm(styles.reveal, 'space-y-8 lg:col-span-7')}
          >
            {details.items.map((item, index) => (
              <li
                key={item.title}
                className={clsxm(styles.rv, 'flex gap-4')}
                style={delay(index * 100)}
              >
                <span className='mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white text-primary-600 shadow-[0_1px_2px_rgba(15,15,40,0.08)] ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/[0.08] dark:text-primary-300 dark:ring-white/10'>
                  <CheckGlyph className='h-3.5 w-3.5' />
                </span>
                <div>
                  <h3
                    className={clsxm(
                      'text-lg font-semibold tracking-tight',
                      tone.ink,
                    )}
                  >
                    {t(item.title, vars)}
                  </h3>
                  <p
                    className={clsxm('mt-1.5 text-[16px] leading-7', tone.body)}
                  >
                    {t(item.body, vars)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Stage>
    </section>
  );
}
